import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {filter, finalize, map, mergeMap} from "rxjs/operators";
import {AccountService} from "src/services/account.service";
import {Account, AccountUser, Plans, Device} from "src/models/account.model";
import {combineLatest, Observable} from "rxjs";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import Swal from "sweetalert2";
import {IDropdownSettings} from "ng-multiselect-dropdown";
import {User} from "../../../../../models/user.model";
import {PlanService} from "../../../../../services/plan.service";
import {UserService} from "../../../../../services/user.service";
import {AngularFireStorage} from "@angular/fire/storage";
import {AuthService} from "../../../../../services/auth.service";

@Component({
  selector: 'app-accounts-edit',
  templateUrl: './accounts-edit.component.html'
})
export class AccountsEditComponent implements OnInit {

  public userForm: FormGroup;
  public deviceForm: FormGroup;

  dropdownUserSettings:IDropdownSettings;
  dropdownPlanSettings:IDropdownSettings;

  get color(): string {
    return this._color;
  }
  set color(color: string) {
    this._color = color !== "light" && color !== "dark" ? "light" : color;
  }
  private _color = "light";

  account: Account = <Account> {};
  userAuth: User;

  user: User = <User> {};
  users: User[] = [];
  usersForDropdown: any[] = [];
  plans: Plans[] = [];

  accountPlans: FormArray;
  accountUsers: FormArray;

  selectedUser: any = [];
  selectedPlan = [];
  plan: Plans = {};
  ownerId: string = "";

  // Device related properties
  devices: Device[] = [];
  isAddingDevice: boolean = false;
  editingDeviceIndex: number = -1;

  percentage: Observable<number>;
  snapshot: Observable<any>;
  downloadURL: Observable<string>;
  photoUrl: any;

  toggleButtonConfig = {
    value: false
  }

  constructor(
    protected route : ActivatedRoute,
    private accountService: AccountService,
    private planService: PlanService,
    private userService: UserService,
    public formBuilder: FormBuilder,
    public router: Router,
    private storage: AngularFireStorage,
    public auth: AuthService
    ) {

    this.auth.user$.subscribe(user => this.userAuth = user);

  }

  async ngOnInit() {

    this.userForm = this.formBuilder.group({
      accountName: [''],
      email: [''],
      address1: [''],
      city: [''],
      province: [''],
      phoneNumber: [''],
      status: [''],
      photoUrl: [''],
      accountUsers: [''],
      plans: [''],
      tin: [''],
      taxType: ['NON-TAX']
    });

    this.deviceForm = this.formBuilder.group({
      device_name: ['', Validators.required],
      device_model: ['', Validators.required],
      device_serial_number: ['', Validators.required]
    });

    this.userForm.patchValue(this.account);

    this.route.paramMap
      .pipe(
        map(pm => pm.get('id')),
        filter(id => id != null),
        mergeMap(id => this.accountService.getAccountById(id)),
      ).subscribe(async (account) => {

      this.account = account;
      this.devices = this.account.devices || [];

      // Get all accounts to check which users are already assigned
      combineLatest([
        this.userService.getUsersAccount(), 
        this.planService.getPlans(),
        this.accountService.getAccounts()
      ])
        .subscribe(async ([users, plans, allAccounts]) => {
          this.users = users;
          this.plans = plans;
          this.selectedPlan = [this.account.plans];
          this.selectedUser = this.account.accountUsers;

          // Process users to mark those already assigned to other accounts
          this.processUsersForDropdown(users, allAccounts);

          this.userForm = this.formBuilder.group({
            accountName: this.account.account_name,
            email: this.account.email_address,
            phoneNumber: this.account.phone_number,
            address1: this.account.address?.street_house_building_number || '',
            city: this.account.address?.city || '',
            province: this.account.address?.province || '',
            status: this.account.status,
            photoUrl: this.account.photoUrl,
            plans: this.account.plans,
            accountUsers: this.account.accountUsers,
            tin: this.account.tin || '',
            taxType: this.account.taxType || 'NON-TAX'
          });

          this.userForm.patchValue(account);
        });

      this.dropdownUserSettings = {
        singleSelection: false,
        idField: 'id',
        textField: 'fullName',
        selectAllText: 'Select All',
        unSelectAllText: 'UnSelect All',
        allowSearchFilter: true,
        searchPlaceholderText: "search user",
        noDataAvailablePlaceholderText: "user not found",
        limitSelection: 10,
        enableCheckAll: false,
        disabledField: 'isDisabled'
      };

      this.dropdownPlanSettings = {
        singleSelection: true,
        idField: 'id',
        textField: 'name',
        selectAllText: 'Select All',
        unSelectAllText: 'UnSelect All',
      };

    });

  }

  // Process users to mark those already assigned to other accounts
  processUsersForDropdown(users: User[], allAccounts: Account[]) {
    this.usersForDropdown = users.map(user => {
      // Check if user is assigned to any other account except the current one
      const isAssignedToOtherAccount = allAccounts.some(acc => {
        if (!acc.accountUsers || acc.id === this.account.id) return false;
        
        return Array.isArray(acc.accountUsers) && acc.accountUsers.some(accUser => accUser.id === user.uid);
      });

      // Add property to show user's assignment status
      const userForDropdown = {
        ...user,
        id: user.uid, // Use uid as id for consistency
        isDisabled: isAssignedToOtherAccount,
        fullName: user.fullName + (isAssignedToOtherAccount ? ' (assigned elsewhere)' : '')
      };
      
      return userForDropdown;
    });
  }

  getPlan(id):Promise<any>{
    return new Promise((resolve) =>{
      this.planService.getPlanById(id).subscribe((plan) =>{
        resolve(plan);
      });
    });
  }

  onSelectedPlan(item: any) {
    let elementPlan = this.plans.map((obj) => {return obj.id; }).indexOf(item.id);
    this.account.plans = this.plans[elementPlan];
  }

  onSelectedUsers(item: any) {
    // No logging needed
  }

  setOwnership(id){
    if(id){
      this.account.owner_id = id;
      this.selectedUser.findIndex((obj: any, i) => {
        this.selectedUser[i].isOwner = obj.id === id;
      });

    }
  }

  get getControl(){
    return this.userForm.controls;
  }

  get getDeviceControl(){
    return this.deviceForm.controls;
  }

  // Device management methods
  toggleAddDevice() {
    this.isAddingDevice = !this.isAddingDevice;
    this.editingDeviceIndex = -1;
    if (!this.isAddingDevice) {
      this.deviceForm.reset();
    }
  }

  addDevice() {
    if (this.deviceForm.valid) {
      const deviceData = this.deviceForm.value;
      
      if (this.editingDeviceIndex >= 0) {
        // Update existing device
        this.devices[this.editingDeviceIndex] = {
          ...this.devices[this.editingDeviceIndex],
          device_name: deviceData.device_name,
          device_model: deviceData.device_model,
          device_serial_number: deviceData.device_serial_number
        };
        this.editingDeviceIndex = -1;
      } else {
        // Add new device
        const newDevice: Device = {
          id: this.generateRandomString(8),
          device_name: deviceData.device_name,
          device_model: deviceData.device_model,
          device_serial_number: deviceData.device_serial_number,
          date_registered: new Date()
        };
        this.devices.push(newDevice);
      }
      
      this.deviceForm.reset();
      this.isAddingDevice = false;
    }
  }

  editDevice(index: number) {
    const device = this.devices[index];
    this.deviceForm.patchValue({
      device_name: device.device_name,
      device_model: device.device_model,
      device_serial_number: device.device_serial_number
    });
    this.editingDeviceIndex = index;
    this.isAddingDevice = true;
  }

  removeDevice(index: number) {
    this.devices.splice(index, 1);
  }

  generateRandomString(length: number) {
    let result = '';
    let characters = '0123456789';
    let charactersLength = characters.length;
    for ( let i = 0; i < length; i++ ) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  createAccountPlans(): FormGroup {
    return this.formBuilder.group({
      plans: [<Plans>{}]
    })
  }

  addPlans() {
    this.accountPlans.push(this.createAccountPlans());
  }

  createAccountUser(): FormGroup {
    return this.formBuilder.group({
      accountUsers: [<AccountUser>{}]
    })
  }

  addAccountUsers() {
    this.accountUsers.push(this.createAccountUser());
  }

  async save(){

    if(this.userForm.valid){
      const formValues = this.userForm.value;
      const sUsers: any = [];
      
      let account: Account =  {
        account_key: this.account.account_key,
        account_name: formValues.accountName,
        owner_id: this.account.owner_id,
        email_address: formValues.email,
        phone_number: formValues.phoneNumber,
        address: {
          street_house_building_number: formValues.address1,
          city: formValues.city,
          province: formValues.province
        },
        status: formValues.status,
        photoUrl: this.account.photoUrl ? this.account.photoUrl : null,
        plans: this.account.plans,
        accountUsers: sUsers,
        devices: this.devices,
        tin: formValues.tin,
        taxType: formValues.taxType
      }

      let isExist = this.selectedUser.findIndex((obj) => { return obj.id === account.owner_id; }) != -1;
      if(!isExist){
        await Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Please assign ownership.'
        })
        return;
      }

      for ( let i = 0; i < this.selectedUser.length; i++ ){

        if(this.selectedUser.length > 1){
          this.selectedUser[i].isOwner = account.owner_id === this.selectedUser[i].id;
        }else{
          account.owner_id = this.selectedUser[i].id;
          this.selectedUser[i].isOwner = true;
        }

        let accountUser: AccountUser = {
          account_key: account.account_key,
          id: this.selectedUser[i].id,
          fullName: this.selectedUser[i].fullName,
          isOwner: this.selectedUser[i].isOwner
        }

        sUsers.push(accountUser);

        await this.accountService.addAccountUser(account.account_key, sUsers);
      }

      await Swal.fire({
        title: 'Saving... please wait...',
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: ()=>{

          Swal.showLoading()

          this.accountService.updateAccount(account, this.account.id).then(() =>{
            Swal.fire({
              title: 'Account successfully updated.',
              timer: 2000,
              icon: 'success',
              didClose: ()=>{
                Swal.hideLoading();
              }
            });
          });

        }

      });

    }else{
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Please check all the required fields.'
      })
    }

  }

  delete(account: Account){
    this.accountService.deleteAccount(account).then(() =>{
      Swal.fire(
        'Success',
        'User deleted!',
        'success'
      )
    });
  }

  uploadImage($event: Event) {

    if(this.auth.canDelete(this.userAuth)){
      let currentDate = Date.now();
      const element = $event.target as HTMLInputElement;
      const file: File = (element.files as FileList)[0];
      const filePath = `company_logo/${currentDate}`;
      const fileRef = this.storage.ref(filePath);
      const task = this.storage.upload(`company_logo/${currentDate}`, file);

      //this.percentage = task.percentageChanges();
      task.snapshotChanges()
        .pipe(
          finalize(() => {
            this.downloadURL = fileRef.getDownloadURL();
            this.downloadURL.subscribe(url => {
              if (url) {
                this.account.photoUrl = url;
                // imageToBase64(url).then((res) =>{
                //   this.account.photoUrl = "data:image/jpeg;base64, " + res;
                //   //this.photoUrl = this.domSanitizer.bypassSecurityTrustUrl(base64) as string;
                // });
              }
            });
          })
        )
        .subscribe(url => {
          if (url) {
            console.log(url);
          }
        });
    }else{
      alert("You have no right to upload image. Please contact your administrator. Thank you!");
    }

  }

}
